import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@app-env/environment';
import { ApiResponse } from '@app/core/models';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, first, mapTo, pluck, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { Store, select } from '@ngrx/store';
import * as InputsStore from '@app/shared/store/inputs';
import { LanguageCode } from '@app/models/page';
import { filterNotNullish } from '@app/core/utils/rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';

type SearchResult = {
	title: string;
	slug: string;
	path: string;
};

type CustomerSupport = {
	title: string;
	phoneNumber: string;
	phoneNumberHelp: string;
	emailAddress: string;
	description: string;
}[];

@Component({
	selector: 'app-header',
	templateUrl: 'header.component.html',
	styleUrls: ['header.component.scss'],
})
export class HeaderComponent implements OnDestroy {
	public isNavbarCollapsed = true;
	public openSearchInput = false;

	public readonly searchInput = new UntypedFormControl();
	public searchResultLoading = false;
	public readonly searchInput$ = new Subject<string>();
	public readonly searchResults$: Observable<SearchResult[]> = concat(
		of([]), // default items
		this.searchInput$.pipe(
			distinctUntilChanged(),
			debounceTime(500),
			tap(() => {
				this.searchResultLoading = true;
			}),
			switchMap(term =>
				this._http.get<ApiResponse<SearchResult[]>>(environment.API_URL + `search/${term}?lang=hu`).pipe(
					pluck('data'),
					tap(() => {
						this.searchResultLoading = false;
					}),
					// map(response => ShippingPriceActions.loadSuccess({ prices: response })),
					catchError((error: HttpErrorResponse) => {
						// Sentry.withScope(scope => {
						// 	scope.setTag('module', 'site');
						// 	scope.setExtra('error', error);
						// 	Sentry.captureMessage('API error');
						// });
						// return of(ShippingPriceActions.loadFailed());
						return of([]);
					})
				)
			)
		)
	);

	public readonly customerSupport$: Observable<CustomerSupport> = this._uiState.pipe(select(InputsStore.selectors.selectLanguage), filterNotNullish(), distinctUntilChanged()).pipe(
		switchMap(lang =>
			this._http
				.get<ApiResponse<{ customerSupport: CustomerSupport }>>(environment.API_URL + `site-data`, {
					params: {
						lang,
					},
				})
				.pipe(
					pluck('data', 'customerSupport'),
					// map(response => ShippingPriceActions.loadSuccess({ prices: response })),
					catchError((error: HttpErrorResponse) => {
						// Sentry.withScope(scope => {
						// 	scope.setTag('module', 'site');
						// 	scope.setExtra('error', error);
						// 	Sentry.captureMessage('API error');
						// });
						// return of(ShippingPriceActions.loadFailed());
						return of([]);
					})
				)
		),
		shareReplay(1)
	);

	public readonly translationsLoaded$ = this._translate.onDefaultLangChange.pipe(first(), mapTo(true), shareReplay(1));

	public isSmallScreen$ = this.breakpointObserver.observe(['(max-width: 991.98px)']);

	private _subscriptions: Subscription[] = [];

	constructor(private _router: Router, private _translate: TranslateService, private _http: HttpClient, private _uiState: Store<InputsStore.State>, private breakpointObserver: BreakpointObserver) {
		this._subscriptions.push(
			this._router.events.subscribe(event => {
				if (event instanceof NavigationEnd) {
					this.isNavbarCollapsed = true;
				}
			})
		);
	}

	public selectLang(lang: LanguageCode): void {
		this._translate.use(lang);
		this._uiState.dispatch(InputsStore.actions.setLanguage({ language: lang }));
		this._router.navigate([`/${lang}`]);
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	onChange(selectedSearchResult: SearchResult): void {
		this._router.navigateByUrl(selectedSearchResult.path);
		this.searchInput.reset(null);
		this.openSearchInput = false;
	}
}
